import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import { submitOnlineOrQueue } from "@/utils/offlineQueue";
import { CheckCircle2, XCircle, AlertCircle, CloudOff } from "lucide-react";

const CHECKPOINTS = [
  { id: 1, en: "Clean machine with cloth", hi: "कपड़े से मशीन साफ करें", criteria: "Visual — No dust, oil, debris" },
  { id: 2, en: "Check nuts, bolts, slide bolt, brake", hi: "नट, बोल्ट, स्लाइड बोल्ट, ब्रेक जाँचें", criteria: "Spanner — All tight, no loose parts" },
  { id: 3, en: "Oiling — main shaft, slide, pedal", hi: "मुख्य शाफ्ट, स्लाइड, पेडल पर तेल लगाएं", criteria: "Manual — Oil applied at all points" },
  { id: 4, en: "Check belt condition", hi: "बेल्ट की स्थिति जाँचें", criteria: "Visual — No cracks, proper tension" },
  { id: 5, en: "Check starter condition", hi: "स्टार्टर की स्थिति जाँचें", criteria: "Visual — Starter working properly" },
  { id: 6, en: "Check pedal spring", hi: "पेडल स्प्रिंग जाँचें", criteria: "Foot test — Spring returns properly" },
  { id: 7, en: "Check machine sound", hi: "मशीन की आवाज़ जाँचें", criteria: "Listen — No unusual noise" },
];

const MachineChecksheet = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(user?.machine_assigned || "");
  const [checkpoints, setCheckpoints] = useState(
    CHECKPOINTS.map((cp) => ({ ...cp, status: null, remark: "" }))
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [alreadyDone, setAlreadyDone] = useState(false);

  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const _now = new Date();
  const _mins = _now.getHours() * 60 + _now.getMinutes();
  // Single shift 09:00–19:30 IST with a 13:00–13:30 break
  const shift = (_mins >= 9 * 60 && _mins < 19 * 60 + 30) ? "Shift 1" : "Off Shift";

  useEffect(() => {
    api.get("/machines").then((res) => setMachines(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedMachine) return;
    api.get(`/machine-check/status?machine_code=${selectedMachine}`)
      .then((res) => setAlreadyDone(res.data.submitted))
      .catch(() => {});
  }, [selectedMachine]);

  const setStatus = (idx, status) => {
    setCheckpoints((prev) => prev.map((cp, i) => i === idx ? { ...cp, status } : cp));
  };

  const setRemark = (idx, remark) => {
    setCheckpoints((prev) => prev.map((cp, i) => i === idx ? { ...cp, remark } : cp));
  };

  const allAnswered = checkpoints.every((cp) => cp.status !== null);

  const handleSubmit = async () => {
    if (!selectedMachine) { setError("Please select a machine"); return; }
    if (!allAnswered) { setError("Please answer all checkpoints"); return; }
    setSubmitting(true);
    setError("");
    try {
      const overall = checkpoints.every((cp) => cp.status === "OK") ? "OK" : "Issue";
      const result = await submitOnlineOrQueue(api, {
        method: "post",
        url: "/machine-check",
        payload: {
          machine_code: selectedMachine,
          operator_name: user.full_name,
          checkpoints: checkpoints.map((cp) => ({ status: cp.status, remark: cp.remark })),
        },
        label: `Checksheet ${selectedMachine}`,
      });
      if (result.online) {
        setSuccess(result.data);
      } else {
        setSuccess({
          check_id: `QUEUED-${selectedMachine}-${Date.now()}`,
          overall_status: overall,
          _queued: true,
        });
        window.__amplOfflineRefresh?.();
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header title="Checksheet" showBack backPath="/home" />
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <div className={`w-20 h-20 ${success._queued ? "bg-amber-100" : "bg-green-100"} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {success._queued
              ? <CloudOff size={48} className="text-amber-600" />
              : <CheckCircle2 size={48} className="text-[#15803D]" />}
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">
            {success._queued ? "Queued — Will Sync" : "Checksheet Submitted!"}
          </h2>
          <p className="text-slate-500 mb-1">
            {success._queued
              ? "Offline — saved on device, will upload when WiFi returns."
              : "मशीन चेकशीट जमा हो गई"}
          </p>
          <div className="bg-white rounded-xl border border-slate-200 p-4 mt-6 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Machine</span>
              <span className="font-bold">{selectedMachine}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Overall Status</span>
              <span className={`font-bold ${success.overall_status === "OK" ? "text-[#15803D]" : "text-[#DC2626]"}`}>
                {success.overall_status}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Check ID</span>
              <span className="font-mono text-xs">{success.check_id}</span>
            </div>
          </div>
          <button
            data-testid="checksheet-done-btn"
            onClick={() => navigate("/home")}
            className="mt-6 w-full h-12 bg-[#1A3560] text-white font-bold rounded-xl active:scale-[0.98] transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <Header title="Machine Checksheet" showBack backPath="/home" />

      <div className="max-w-md mx-auto px-4 py-4">
        {/* Form header */}
        <div className="bg-[#1A3560] rounded-2xl p-4 text-white mb-4">
          <p className="text-xs uppercase tracking-widest text-white/60 mb-1">AMPL/QMS/F35</p>
          <h2 className="font-extrabold text-lg">Machine Pre-Start Checksheet</h2>
          <p className="text-xs text-white/60 font-medium">मशीन प्री-स्टार्ट चेकशीट</p>
          <div className="flex gap-4 mt-3 text-sm">
            <div><span className="text-white/50">Date</span> <span className="font-bold ml-1">{today}</span></div>
            <div><span className="text-white/50">Shift</span> <span className="font-bold ml-1">{shift}</span></div>
          </div>
        </div>

        {/* Machine select */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
            Select Machine / मशीन चुनें
          </label>
          <select
            data-testid="machine-select"
            value={selectedMachine}
            onChange={(e) => setSelectedMachine(e.target.value)}
            className="w-full h-12 px-3 rounded-lg border border-slate-300 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1A3560]"
          >
            <option value="">-- Select Machine --</option>
            {machines.map((m) => (
              <option key={m.machine_code} value={m.machine_code}>
                {m.machine_code} ({m.tonnage}T)
              </option>
            ))}
          </select>
          {alreadyDone && (
            <div className="mt-2 flex items-center gap-2 text-amber-600 text-sm bg-amber-50 rounded-lg px-3 py-2">
              <AlertCircle size={16} />
              Checksheet already submitted for today's {shift}
            </div>
          )}
        </div>

        {/* Operator */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
            Operator / ऑपरेटर
          </label>
          <p className="font-bold text-slate-900">{user?.full_name}</p>
          <p className="text-xs text-slate-400">{user?.user_id}</p>
        </div>

        {/* Checkpoints */}
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
          Checkpoints / जाँच बिंदु ({checkpoints.filter(cp => cp.status !== null).length}/7)
        </p>

        {checkpoints.map((cp, idx) => (
          <div
            key={cp.id}
            data-testid={`checkpoint-${cp.id}`}
            className={`bg-white rounded-2xl border-2 mb-3 overflow-hidden transition-all ${
              cp.status === "OK" ? "border-[#15803D]" :
              cp.status === "Issue" ? "border-[#DC2626]" : "border-slate-200"
            }`}
          >
            <div className="p-4">
              <div className="flex items-start gap-3 mb-3">
                <span className="flex-shrink-0 w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold text-slate-600">
                  {cp.id}
                </span>
                <div className="flex-1">
                  <p className="font-bold text-slate-900 text-sm leading-tight">{cp.en}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{cp.hi}</p>
                  <p className="text-slate-400 text-xs mt-1 italic">{cp.criteria}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid={`cp-${cp.id}-ok`}
                  onClick={() => setStatus(idx, "OK")}
                  className={`h-14 rounded-xl font-bold text-base flex items-center justify-center gap-2 active:scale-[0.97] transition-all ${
                    cp.status === "OK"
                      ? "bg-[#15803D] text-white shadow-md"
                      : "bg-green-50 text-[#15803D] border-2 border-[#15803D]"
                  }`}
                >
                  <CheckCircle2 size={20} /> OK / ठीक है
                </button>
                <button
                  data-testid={`cp-${cp.id}-issue`}
                  onClick={() => setStatus(idx, "Issue")}
                  className={`h-14 rounded-xl font-bold text-base flex items-center justify-center gap-2 active:scale-[0.97] transition-all ${
                    cp.status === "Issue"
                      ? "bg-[#DC2626] text-white shadow-md"
                      : "bg-red-50 text-[#DC2626] border-2 border-[#DC2626]"
                  }`}
                >
                  <XCircle size={20} /> Issue / समस्या
                </button>
              </div>

              {cp.status === "Issue" && (
                <textarea
                  data-testid={`cp-${cp.id}-remark`}
                  value={cp.remark}
                  onChange={(e) => setRemark(idx, e.target.value)}
                  placeholder="Describe the issue / समस्या बताएं..."
                  rows={2}
                  className="mt-3 w-full px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#DC2626] resize-none"
                />
              )}
            </div>
          </div>
        ))}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm font-medium flex items-center gap-2">
            <AlertCircle size={16} /> {error}
          </div>
        )}
      </div>

      {/* Sticky submit */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-md mx-auto">
          <button
            data-testid="checksheet-submit-btn"
            onClick={handleSubmit}
            disabled={!allAnswered || !selectedMachine || submitting}
            className="w-full h-14 bg-[#1A3560] disabled:bg-slate-300 text-white font-bold text-base rounded-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Submitting...</>
            ) : (
              "Submit Checksheet / जमा करें"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MachineChecksheet;
